import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Button,
  TextField,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  CircularProgress,
  ImageList,
  ImageListItem,
  Skeleton,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
} from "@mui/material";

import LoadingDialog from "./LoadingDialog";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import ImageIcon from "@mui/icons-material/Image";
import TimelineIcon from "@mui/icons-material/Timeline"; // Add this import
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MoreToCome from "./MoreToCome";
import WelcomeDialogLessons from "./WelcomeDialogLessons";
import SaveLessonDialog from "./SaveLessonDialog";
import { useSearchParams, useParams } from "react-router-dom";
import LessonDrawer from "./LessonDrawer";
import ShareLink from "./ShareLink";

import PrintAsPdf from "./PrintAsPdf";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import LoadingBackdrop from "./LoadingBackdrop";
import ShareDialog from "./ShareDialog";
import ShareIcon from "@mui/icons-material/Share";
import Translate from "./Translate";
import SlideInPaper from "./SlideInPaper";
import TimeBuilderEditDialog from "./TimeBuilderEditDialog";
import { callApi } from "./api";
import { BackButton } from "./functions";
import { AnimatedStep } from "./Animations";
import CustomTimeline from "./Timeline";

import MindMap from "./MindMap";
import TextFieldsIcon from "@mui/icons-material/TextFields";

import LanguageSelector from "./LanguageSelector";
import ClearIcon from "@mui/icons-material/Clear";

import { db } from "./firebase";
import {
  collection,
  query,
  where,
  limit,
  getDocs,
  updateDoc,
  orderBy,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

import { generateAndProcessImage, trackLesson } from "./functions";
import { Mosaic } from "react-loading-indicators";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DownloadIcon from "@mui/icons-material/GetApp";
import PrintIcon from "@mui/icons-material/Print";
import { useSnackbar } from "./SnackbarHandler";

import { keyframes } from "@emotion/react";
import InfoBox from "./InfoBox";
import CopyIcon from "@mui/icons-material/FileCopy";
import { Palette as PaletteIcon } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Logger from "./Logger";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import CodeIcon from "@mui/icons-material/Code";
import {
  Cake,
  Castle,
  Anchor,
  Flag,
  Store,
  DirectionsBoat,
  EmojiEvents,
  Security,
  Agriculture,
  Pets,
  AccountBalance,
  LocalGroceryStore,
  Public,
  History,
} from "@mui/icons-material";

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;
const themes = [
  { id: 3, name: "Lejon", description: "Ett mäktigt lejon", icon: Pets },
  {
    id: 1,
    name: "Prinsesstårta",
    description: "En klassisk svensk bakelse",
    icon: Cake,
  },

  {
    id: 7,
    name: "Gamla Stan",
    description: "Stockholms historiska centrum",
    icon: AccountBalance,
  },
  {
    id: 8,
    name: "Vikingaskepp",
    description: "Ett mäktigt vikingaskepp",
    icon: DirectionsBoat,
  },
];
const ColoringLesson = ({ user, theme, styles, setMinimized }) => {
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [customMode, setCustomMode] = useState(true);
  const [customPrompt, setCustomPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [buttonKey, setButtonKey] = useState(0); // Use key to force re-render and restart animation
  const { showSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [lessonData, setLessonData] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [shareDialogOpenFriends, setShareDialogOpenFriends] = useState(false);

  const pdfButtonRef = useRef(null);
  const translateButtonRef = useRef(null);
  const shareLinkButtonRef = useRef(null);

  const { lessonId } = useParams();

  const viewMode = searchParams.get("v") === "t";
  useEffect(() => {
    fetchImages();
  }, [user.uid]);

  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    Logger.log("lessonData ColoringLesson: ", lessonData);
    if (lessonData) {
      setCurrentStep(2);
      setMinimized(true);
    } else {
      setMinimized(false);
    }
  }, [lessonData, currentStep]);

  const toggleViewMode = () => {
    const currentParams = Object.fromEntries(searchParams.entries());
    if (currentParams.v === "t") {
      delete currentParams.v;
    } else {
      currentParams.v = "t";
    }
    setSearchParams(currentParams);
  };

  useEffect(() => {
    const loadLessonFromId = async () => {
      if (lessonId) {
        try {
          const q = query(
            collection(db, "lesson_usage"),
            where("__name__", ">=", lessonId),
            where("__name__", "<=", lessonId + "\uf8ff"),
            where("lessonType", "==", "ColoringLesson.js"),
            limit(1),
          );

          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const docData = querySnapshot.docs[0].data();

            if (!docData.hasOpened) {
              await updateDoc(querySnapshot.docs[0].ref, {
                hasOpened: true,
                openedAt: Date.now(),
              });
            }
            setLessonData(docData.data);

            // Scroll to lesson content

            showSnackbar("Lektionen har laddats in.", "success");
          } else {
            showSnackbar("Kunde inte hitta lektionen", "error");
          }
        } catch (error) {
          console.error("Error loading lesson:", error);
          showSnackbar("Ett fel uppstod när lektionen skulle laddas", "error");
        }
      }
    };

    loadLessonFromId();
  }, [lessonId]);

  const handleSaveLessonClick = () => {
    setSaveDialogOpen(true);
  };

  const handleSaveWithComment = async (comment, name, coverImage, tags) => {
    Logger.log(JSON.stringify(lessonData, null, 2));
    await trackLesson(
      user.uid,
      "ColoringLesson.js",
      lessonData,
      null,
      true,
      comment,
      name,
      coverImage,
      tags,
    );
    setDrawerOpen(true);
    showSnackbar("Lektionen sparad i din lektionsbank!", "success");
  };

  const handleShare = async (
    lessonComment,
    lessonName,
    selectedImage,
    tags,
    shareData,
  ) => {
    setIsSharing(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));

    try {
      // Create a document for each student and then create notifications with the doc IDs
      const sharePromises = shareData.studentIds.map(async (studentId) => {
        const documentData = {
          userId: user.uid,
          shared: true,
          sharedTo: studentId,
          comment: shareData.comment,
          sharedAt: Date.now(),
          fileUrl: shareData.fileUrl,
          lessonType: "ColoringLesson.js",
          timestamp: serverTimestamp(),
          lessonComment,
          lessonName,
          coverImage: selectedImage,
          hasPrinted: true,
          data: lessonData,
        };

        // Create the lesson document and get its ID
        const lessonDoc = await addDoc(
          collection(db, "lesson_usage"),
          documentData,
        );

        Logger.log(
          "Adding document for student",
          studentId,
          ":",
          JSON.stringify(documentData, null, 2),
        );

        // Create notification with the lesson doc ID
        await addDoc(collection(db, "notifications"), {
          type: "lesson_shared",
          recipientId: studentId,
          senderId: user.uid,
          read: false,
          createdAt: serverTimestamp(),
          lessonData: {
            lessonId: lessonDoc.id,
            lessonType: documentData.lessonType,
            lessonName: documentData.lessonName,
            fileUrl: documentData.fileUrl,
            comment: documentData.comment,
            coverImage: selectedImage,
          },
        });

        return lessonDoc;
      });

      await Promise.all(sharePromises);
      showSnackbar(`Du har nu delat lektionen!`, "success");
    } catch (error) {
      showSnackbar("Fel vid delande av lektionen: " + error.message, "error");
      console.error(error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleCopyPrompt = () => {
    if (selectedImage) {
      navigator.clipboard.writeText(selectedImage.prompt);
      showSnackbar("Prompt kopierad!", "success");
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setDialogOpen(true);
  };

  const fetchImages = async () => {
    try {
      const imagesRef = collection(db, "images");
      const q = query(
        imagesRef,
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc"),
        limit(17), // Add this line to limit to 16 images
      );
      const querySnapshot = await getDocs(q);
      const images = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setGeneratedImages(images);
    } catch (error) {
      console.error("Fel vid hämtning av bilder:", error);
      showSnackbar("Kunde inte hämta bilder. Vänligen försök igen.", "error");
    }
  };
  const handleThemeSelect = (item) => {
    setSelectedTheme(item);
    setButtonKey((prev) => prev + 1); // Change key to trigger animation
  };

  const handleGenerate = async () => {
    if (!customPrompt.trim()) {
      showSnackbar("Du måste beskriva målarbilden.", "info");
      return;
    }

    setIsLoading(true);
    const basePrompt = customMode
      ? `generera en målarbild av en ${customPrompt} som elever ska fylla i med färg själva. så det måste finnas mycket tomrum i bilden.`
      : `generera en målarbild av en ${selectedTheme.name} (${selectedTheme.description})  som elever ska fylla i med färg själva. så det måste finnas mycket tomrum i bilden.`;

    try {
      let imageUrl = await generateAndProcessImage(basePrompt, user.uid, {
        size: "1024x1024",
        model: "dall-e-3",
        quality: "standard",
        responseFormat: "url",
      });

      await fetchImages();
      showSnackbar("Målarbild genererad!", "success");

      await trackLesson(user.uid, "ColoringLesson.js", null, {
        basePrompt,
      });

      setLessonData(imageUrl);
    } catch (error) {
      console.error("Error generating coloring page:", error);
      showSnackbar("Kunde inte generera målarbild. Försök igen.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (imageToDownload) => {
    // Use the passed image or fall back to the latest image
    const imageUrl = imageToDownload?.imageUrl || generatedImages[0]?.imageUrl;

    Logger.log("imageUrl: ", imageUrl);

    if (imageUrl) {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `malarbild-studera.ai-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showSnackbar("Bild nedladdad!", "success");
      } catch (error) {
        console.error("Error downloading image:", error);
        showSnackbar("Kunde inte ladda ner bilden. Försök igen.", "error");
      }
    }
  };

  const handlePrint = () => {
    if (generatedImages.length > 0) {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Skriv ut målarbild</title>
            <style>
              body {
                margin: 0;
                padding: 20px;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              @media print {
                body {
                  padding: 0;
                }
              }
            </style>
          </head>
          <body>
            <img src="${generatedImages[0].imageUrl}" alt="Målarbild" />
            <script>
              window.onload = () => {
                window.print();
                window.onafterprint = () => window.close();
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const renderImageOrSkeleton = () => {
    if (isLoading) {
      return (
        <Fade in={true} timeout={2000}>
          <Box sx={{ position: "relative", width: "100%", height: "500px" }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={500}
              animation="wave"
              sx={{ borderRadius: "7px" }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Mosaic
                color={["#33CCCC", "#33CC36", "#B8CC33", "#1097DA"]}
                size="large"
              />
            </Box>
          </Box>
        </Fade>
      );
    }

    if (generatedImages.length > 0) {
      const latestImage = generatedImages[0];
      return (
        <Box
          onClick={() => handleImageClick(latestImage)}
          sx={{ cursor: "pointer", textAlign: "center" }}
        >
          <img
            src={latestImage.imageUrl}
            alt={latestImage.prompt}
            loading="lazy"
            style={{
              width: "700px",
              borderRadius: "7px",
              marginBottom: "16px",
            }}
          />
        </Box>
      );
    }

    return null;
  };

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "100%" },
          margin: "0 auto",
        }}
      >
        {!viewMode && (
          <>
            {currentStep !== 2 && (
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  ...styles.newH1,
                }}
              >
                Målarbilder
              </Typography>
            )}
          </>
        )}
        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          {!viewMode && (
            <>
              <Tooltip title="Dina sparade lektioner">
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  sx={{ ...styles.iconButtonBg }}
                >
                  <BookmarkBorderIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>

        {/*
        <InfoBox
          icon={PaletteIcon}
          title="Powerpoint Instructions"
          instructions={[
            "Generera målarbilder som eleven färglägger",
            "Skriv in egna instruktioner till AI-modellen",
            "Ladda ned eller skriv ut bild",
          ]}
          theme={theme}
          styles={styles}
          user={user}
          lessonType="ColoringLesson.js"
        />
*/}

        {!viewMode && (
          <>
            {currentStep === 1 && (
              <>
                <AnimatedStep isRevealed={true}>
                  <Box sx={{ ...styles.headlineLessonBox, mt: 6 }}>
                    <Typography
                      variant="h6"
                      sx={{ ...styles.headlineLessonBoxTitle }}
                    >
                      <span>1</span> Beskriv målarbilden
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      ...styles.freshBox,
                      padding: "30px",
                      paddingBottom: "10px",
                      width: { xs: "100%", md: "70%" },
                      margin: "0 auto",
                    }}
                  >
                    <Box sx={{ mb: 4, textAlign: "center" }}>
                      {customMode && (
                        <TextField
                          fullWidth
                          multiline
                          InputProps={{
                            style: {
                              fontWeight: "500",
                              fontSize: "1.05rem",
                            },
                          }}
                          autoFocus
                          rows={3}
                          value={customPrompt}
                          onChange={(e) => setCustomPrompt(e.target.value)}
                          placeholder="T.ex. ett lejon som sover på savannen med sina ungar i bakgrunden."
                          sx={{ mt: 2 }}
                        />
                      )}

                      <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
                        {themes.map((item) => {
                          const IconComponent = item.icon;
                          const isSelected = selectedTheme?.id === item.id;

                          return (
                            <Grid item xs={12} sm={6} md={3} key={item.id}>
                              <Box
                                onClick={() => setCustomPrompt(item.name)}
                                sx={{
                                  ...styles.freshBox,

                                  borderRadius: "12px",
                                  padding: "6px",
                                  paddingLeft: "12px",
                                  paddingRight: "12px",

                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  transition: "all 0.3s ease",

                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(255, 255, 255, 0.1)"
                                        : "rgba(139, 92, 246, 0.1)",
                                    transform: "translateY(-2px)",
                                    "& .icon": {
                                      color:
                                        theme.palette.mode === "dark"
                                          ? "rgb(16, 151, 218)"
                                          : "#6b46c1",
                                    },
                                  },
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    textAlign: "center",

                                    fontSize: "0.9rem",
                                  }}
                                >
                                  {item.name}
                                </Typography>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>

                      <Box>
                        <Button
                          key={buttonKey} // Key changes force component remount, restarting animation
                          variant={
                            theme.palette.mode === "light"
                              ? "purp"
                              : "contained"
                          }
                          onClick={handleGenerate}
                          disabled={
                            isLoading || (!customMode && !selectedTheme)
                          }
                          startIcon={
                            !isLoading ? (
                              <GenerateIcon />
                            ) : (
                              <CircularProgress size={20} />
                            )
                          }
                          sx={{
                            mt: 6,
                            animation: `${pulseAnimation} 0.4s ease-in-out`, // Animation always runs on mount
                            "&:not(:disabled)": {
                              transform: "translateY(0)",
                              transition: "transform 0.3s ease",
                              "&:hover": {
                                transform: "translateY(-2px)",
                              },
                            },
                          }}
                        >
                          {isLoading
                            ? "Genererar målarbild..."
                            : "Generera målarbild"}
                        </Button>
                      </Box>
                    </Box>

                    <Box sx={{ mb: 4, display: "none" }}>
                      {renderImageOrSkeleton()}
                    </Box>
                  </Box>
                </AnimatedStep>
              </>
            )}
          </>
        )}
        {currentStep === 2 && (
          <>
            <BackButton
              currentStep={currentStep}
              onClick={() => {
                setCurrentStep(currentStep - 1);

                setLessonData(null);
              }}
              show={currentStep !== 1}
            />
            <AnimatedStep isRevealed={true}>
              <Box sx={{ ...styles.headlineLessonBox, mt: viewMode ? 0 : 5 }}>
                <Typography
                  variant="h6"
                  sx={{ ...styles.headlineLessonBoxTitle, display: "none" }}
                >
                  <span>2</span> Beskriv målarbilden
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexDirection: {
                    xs: "column",
                    md: "row",
                  },
                }}
              >
                <Box
                  sx={{
                    ...styles.freshBox,
                    padding: "0px",
                    paddingTop: "0px",
                    ...(viewMode ? { flex: "0 0 70%" } : { flex: "1 1 auto" }),
                    margin: "0 auto",
                  }}
                >
                  <img
                    src={selectedImage?.imageUrl}
                    alt={selectedImage?.prompt}
                    style={{
                      width: "100%",
                      height: "auto",
                      borderRadius: "4px",
                    }}
                  />

                  <img
                    src={lessonData}
                    style={{
                      width: "100%",
                      height: "auto",
                      borderRadius: "12px",
                    }}
                  />
                </Box>

                {!viewMode && (
                  <Box sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        ...styles.lessonControlPanelNuevo2,

                        "& .MuiButton-root": {
                          ...styles.lessonControlPanelNewButtons2,
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                        }}
                      >
                        Spara
                      </Typography>

                      <Button
                        variant="print"
                        startIcon={<FileDownloadIcon />}
                        sx={{ mt: 2, ml: 2 }}
                        onClick={handleDownload}
                      >
                        Ladda ned bilden
                      </Button>

                      <Button
                        variant="print"
                        startIcon={<SaveAsIcon />}
                        sx={{ mt: 2, ml: 2 }}
                        onClick={handleSaveLessonClick}
                      >
                        Spara i lektionsbanken
                      </Button>
                      <Button
                        variant="print"
                        startIcon={<PrintIcon />}
                        sx={{ mt: 2, ml: 2 }}
                        onClick={handlePrint}
                      >
                        Skriv ut
                      </Button>

                      <PrintAsPdf
                        buttonRef={pdfButtonRef}
                        onPrint={() => handlePrint()}
                        sx={{ ml: 2 }}
                      />

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                          mt: 3,
                        }}
                      >
                        Dela
                      </Typography>
                      <ShareLink
                        buttonRef={shareLinkButtonRef}
                        key={lessonId}
                        lessonId={lessonId}
                        lessonData={lessonData}
                        user={user}
                        lessonType={"ColoringLesson.js"}
                        onCopy={(message, severity) =>
                          showSnackbar(message, severity)
                        }
                      />
                      <Button
                        variant="success"
                        sx={{
                          mt: 2,
                          ml: 2,
                        }}
                        onClick={() => setShareDialogOpen(true)}
                        startIcon={<ShareIcon />}
                      >
                        Dela med elever
                      </Button>

                      <Button
                        variant="success"
                        sx={{}}
                        onClick={() => setShareDialogOpenFriends(true)}
                        startIcon={<ShareIcon />}
                      >
                        Dela med vänner
                      </Button>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                          mt: 3,
                        }}
                      >
                        Visa
                      </Typography>

                      <Button
                        variant="success"
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                          toggleViewMode();
                        }}
                        sx={{ ml: 2, mt: 2 }}
                        startIcon={
                          searchParams.get("v") === "t" ? (
                            <PersonOutlineIcon />
                          ) : (
                            <PersonOutlineIcon />
                          )
                        }
                      >
                        {searchParams.get("v") === "t"
                          ? "Redigeringsläge"
                          : "Elevläge"}
                      </Button>

                      <Box>
                        <MoreToCome user={user} />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </AnimatedStep>
          </>
        )}
      </Box>
      <LessonDrawer
        userId={user.uid}
        lessonType={"ColoringLesson.js"}
        theme={theme}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        lessonIcon={MenuBookIcon}
        onSelectLesson={(lesson) => {
          setLessonData(lesson.data);
          setDrawerOpen(false);
        }}
      />

      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        onShare={handleShare}
        user={user}
        isLessonSharing={true}
        theme={theme}
        lessonName={lessonData?.title || "Målarbild"}
        lessonObject={lessonData}
        lessonType={"ColoringLesson.js"}
      />

      <ShareDialog
        open={shareDialogOpenFriends}
        onClose={() => setShareDialogOpenFriends(false)}
        onShare={handleShare}
        user={user}
        isLessonSharing={true}
        theme={theme}
        lessonName={lessonData?.title || "Målarbild"}
        lessonObject={lessonData}
        lessonType={"ColoringLesson.js"}
        shareWithFriends={true}
      />

      <SaveLessonDialog
        key={lessonData}
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSave={handleSaveWithComment}
        theme={theme}
        lessonName={lessonData?.title || ""}
        lessonObject={lessonData}
        lessonType={"ColoringLesson.js"}
      />

      <LoadingBackdrop
        isOpen={isSharing}
        imagePath="/img/AI.png"
        loadingText={"Delar lektionen..."}
      />

      <LoadingDialog
        open={isLoading}
        onClose={() => {}}
        progressLabel={"Genererar målarbilden..."}
        title="Genererar målarbilden. Snart klart..."
        snackbarMessage="Genereringen är snart klar. Vänligen vänta."
        hideProgressBar={true}
        snackbarSeverity="info"
        progressBarHeight="15px"
        progressBarFontSize="0.9rem"
        coffee={false}
      />

      <WelcomeDialogLessons
        key={currentStep}
        maxWidth={830}
        lessonType="ColoringLesson.js"
        open={currentStep === 2}
        title="Välkommen till målarbilder!"
        imageSrc="https://sportkuriren.se/wp-content/uploads/mona-lisa.png"
        imageSrcMaxWidth="50%"
        items={[
          {
            icon: "Print",
            text: "Du kan antingen ladda ned eller skriv ut bilden",
            color: "#ff9e7d",
          },
          {
            icon: "SaveAs",
            text: "Glöm inte att spara målarbilden i din lektionsbank så du bygger upp ditt eget bildarkiv",
            color: "#ff9e7d",
          },
          {
            icon: "Share",
            text: "Dela målarbilden via en länk som elever (inget elevkonto krävs) kan gå in på  eller via Studera.AI (elevkonto krävs)",

            color: "#ff6b6b",
          },
        ]}
      />
    </Box>
  );
};

export default ColoringLesson;
